import { useState, useMemo } from 'react'
import { AlertCircle, Check, Mic, Search, Sparkles, X, Clock, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { useRecordings, useRecordingNotes } from '@/hooks/useRecordings'
import { formatDuration, formatRecordedAt } from '@/lib/dates'
import { getRecordingDisplayName } from '@/lib/recordingDisplayName'
import type { Recording, Note, DocumentationAction } from '@/types/documentation'

interface RecordingAttachmentProps {
  recording: Recording | null
  dispatch: React.Dispatch<DocumentationAction>
  visitId?: string
  visitScheduledAt?: string
}

function RecordingPreview({ recordingId }: { recordingId: string }) {
  const { data, error } = useRecordingNotes(recordingId)

  if (error) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-red-600">
        <AlertCircle className="h-3 w-3" />
        Failed to load notes
      </div>
    )
  }

  if (!data || data.notes.length === 0) {
    return <p className="text-xs text-slate-400">No notes generated yet</p>
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-500">
        {data.total} note{data.total !== 1 ? 's' : ''}
      </p>
      {data.notes.map((note) => (
        <div key={note.id} className="flex items-center gap-1.5">
          <FileText className="h-3 w-3 text-slate-400" />
          <span className="text-xs text-slate-600">{note.template_name}</span>
        </div>
      ))}
    </div>
  )
}

function RecordingPickerDialog({
  onSelect,
  open,
  onOpenChange,
}: {
  onSelect: (recording: Recording, notes: Note[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data, isLoading, error } = useRecordings({ status: 'completed' })
  const { data: notesData } = useRecordingNotes(selectedId ?? undefined)

  const recordings = data?.recordings ?? []
  const filtered = recordings.filter((r) =>
    getRecordingDisplayName(r).toLowerCase().includes(search.toLowerCase()),
  )

  const selectedRecording = recordings.find((r) => r.id === selectedId)

  const handleAttach = () => {
    if (selectedRecording) {
      onSelect(selectedRecording, notesData?.notes ?? [])
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Attach Recording</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search recordings..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search recordings"
            className="pl-9"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ScrollArea className="h-[300px] rounded-md border" aria-label="Recording list">
            {error ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <p className="text-sm text-red-600">Failed to load recordings</p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">No recordings found</p>
            ) : (
              <div className="p-2 space-y-1">
                {filtered.map((recording) => (
                  <button
                    key={recording.id}
                    onClick={() => setSelectedId(recording.id)}
                    className={`w-full rounded-md p-3 text-left transition-colors ${
                      selectedId === recording.id
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-slate-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        {selectedId === recording.id && (
                          <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                        )}
                        <span className="text-sm font-medium text-slate-900 line-clamp-1">
                          {getRecordingDisplayName(recording)}
                        </span>
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-xs">
                        {formatDuration(recording.duration)}
                      </Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      {formatRecordedAt(recording.recorded_at)}
                    </div>
                    {recording.notes_count > 0 && (
                      <p className="mt-1 text-xs text-slate-400">
                        {recording.notes_count} note{recording.notes_count !== 1 ? 's' : ''}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>

          <div className="rounded-md border p-4">
            {selectedId ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-900">Preview</h4>
                {selectedRecording && (
                  <>
                    {selectedRecording.transcript && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">
                          Transcript
                        </p>
                        <p className="text-xs text-slate-600 line-clamp-6">
                          {selectedRecording.transcript}
                        </p>
                      </div>
                    )}
                    <RecordingPreview recordingId={selectedId} />
                  </>
                )}
                <Button onClick={handleAttach} size="sm" className="w-full">
                  Attach Recording
                </Button>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-slate-400">
                  Select a recording to preview
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function SmartSuggestion({
  suggestion,
  onConfirm,
  onChooseDifferent,
}: {
  suggestion: Recording
  onConfirm: () => void
  onChooseDifferent: () => void
}) {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-medium text-blue-800">
          Suggested Recording
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
          <Mic className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">
            {getRecordingDisplayName(suggestion)}
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>{formatDuration(suggestion.duration)}</span>
            <span>·</span>
            <span>{formatRecordedAt(suggestion.recorded_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onConfirm}>
          Confirm
        </Button>
        <Button size="sm" variant="outline" onClick={onChooseDifferent}>
          Choose Different
        </Button>
      </div>
    </div>
  )
}

export default function RecordingAttachment({
  recording,
  dispatch,
  visitId,
  visitScheduledAt,
}: RecordingAttachmentProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data: recordingsData } = useRecordings({ status: 'completed' })
  const recordings = recordingsData?.recordings ?? []

  // Find best matching recording for this visit
  const suggestedRecording = useMemo(() => {
    if (recording || !visitId) return null

    // First: exact match by visit_id
    const exact = recordings.find((r) => r.visit_id === visitId)
    if (exact) return exact

    // Fallback: closest recorded_at to visitScheduledAt within 2-hour window
    if (!visitScheduledAt) return null
    const scheduledTime = new Date(visitScheduledAt).getTime()
    const twoHours = 2 * 60 * 60 * 1000

    let best: Recording | null = null
    let bestDiff = Infinity
    for (const r of recordings) {
      const diff = Math.abs(new Date(r.recorded_at).getTime() - scheduledTime)
      if (diff < twoHours && diff < bestDiff) {
        bestDiff = diff
        best = r
      }
    }
    return best
  }, [recording, visitId, visitScheduledAt, recordings])

  // Prefetch notes for the suggested recording
  const { data: suggestedNotesData } = useRecordingNotes(suggestedRecording?.id)

  const handleSelect = (rec: Recording, notes: Note[]) => {
    dispatch({
      type: 'ATTACH_RECORDING',
      recording: rec,
      note: notes[0] ?? null,
    })
  }

  const handleConfirmSuggestion = () => {
    if (!suggestedRecording) return
    dispatch({
      type: 'ATTACH_RECORDING',
      recording: suggestedRecording,
      note: suggestedNotesData?.notes?.[0] ?? null,
    })
  }

  const handleDetach = () => {
    dispatch({ type: 'DETACH_RECORDING' })
  }

  if (recording) {
    return (
      <div className="rounded-lg border bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Mic className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">
                {getRecordingDisplayName(recording)}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>{formatDuration(recording.duration)}</span>
                <span>·</span>
                <span>{formatRecordedAt(recording.recorded_at)}</span>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDetach}>
            <X className="h-4 w-4 mr-1" />
            Detach
          </Button>
        </div>
      </div>
    )
  }

  // Show smart suggestion if available
  if (suggestedRecording) {
    return (
      <>
        <SmartSuggestion
          suggestion={suggestedRecording}
          onConfirm={handleConfirmSuggestion}
          onChooseDifferent={() => setDialogOpen(true)}
        />
        <RecordingPickerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSelect={handleSelect}
        />
      </>
    )
  }

  return (
    <div className="rounded-lg border border-dashed bg-white p-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
          <Mic className="h-6 w-6 text-slate-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">No recording attached</p>
          <p className="text-xs text-slate-500">
            Attach a recording to populate the clinical note
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
          Attach Recording
        </Button>
      </div>
      <RecordingPickerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSelect={handleSelect}
      />
    </div>
  )
}
